import { Link, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { 
  Trophy, Users, Calendar, AlertCircle,
  ChevronRight, User, UsersRound, Plus
} from 'lucide-react'
import type { SeasonWithBrackets } from './types'
import { formatDate } from './utils'

interface ActiveSeasonsSectionProps {
  groupId: string
  activeSeasons: SeasonWithBrackets[]
  leagueCount: number
  loading: boolean
  error: string | null
  canManageGroup: boolean
}

export function ActiveSeasonsSection({
  groupId,
  activeSeasons,
  leagueCount,
  loading,
  error,
  canManageGroup,
}: ActiveSeasonsSectionProps) {
  const navigate = useNavigate()

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-7 w-40" />
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-5 w-5 rounded" />
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </div>
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-5 w-5" />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <Skeleton className="h-4 w-36" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-24 rounded-md" />
                  <Skeleton className="h-6 w-28 rounded-md" />
                </div>
                <Skeleton className="h-4 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold flex items-center gap-2">
        <Trophy className="h-5 w-5 text-primary" />
        Active Seasons
      </h2>
      
      {error ? (
        <Card className="p-8 text-center">
          <AlertCircle className="h-10 w-10 mx-auto mb-3 text-destructive" />
          <h3 className="font-semibold mb-1">Failed to load data</h3>
          <p className="text-sm text-muted-foreground">{error}</p>
        </Card>
      ) : activeSeasons.length === 0 ? (
        <Card className="p-8 text-center">
          <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-semibold mb-2">No active seasons</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {canManageGroup 
              ? "Create a league and start a season to begin tracking rankings."
              : "This group doesn't have any active seasons right now."
            }
          </p>
          {canManageGroup && (
            <Button onClick={() => navigate(`/group/${groupId}/admin#leagues`)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First League
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {activeSeasons.map((season) => (
            <Link key={season.seasonId} to={`/group/${groupId}/league/${season.leagueId}`}>
              <Card className="group cursor-pointer h-full hover:border-primary/50 transition-colors">
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-2">
                    {/* Left side: Info */}
                    <div className="min-w-0 flex-1 space-y-2">
                      {/* League / Season - same line */}
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <div className="flex items-center gap-1.5">
                          <Trophy className="h-4 w-4 text-primary shrink-0" />
                          <span className="font-semibold">{season.league.name}</span>
                        </div>
                        <span className="text-muted-foreground">/</span>
                        <span className="font-medium">{season.name}</span>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full shrink-0 ${
                          season.league.format === 'singles'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                        }`}>
                          {season.league.format === 'singles' ? (
                            <User className="h-3 w-3" />
                          ) : (
                            <UsersRound className="h-3 w-3" />
                          )}
                          <span className="hidden sm:inline">{season.league.format === 'singles' ? 'Singles' : 'Doubles'}</span>
                        </span>
                      </div>

                      {/* Date range + Brackets */}
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(season.startDate)} – {formatDate(season.endDate)}
                        </span>
                        {season.brackets.length > 0 && (
                          <>
                            <span>·</span>
                            {season.brackets.map((bracket) => (
                              <span
                                key={bracket.bracketId}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted"
                              >
                                <span>{bracket.name}</span>
                                <span className="flex items-center gap-0.5 text-muted-foreground">
                                  <Users className="h-2.5 w-2.5" />
                                  {bracket.playerCount}
                                </span>
                              </span>
                            ))}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Right side: Arrow */}
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors shrink-0 mt-0.5" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* All Leagues link for admins */}
      {canManageGroup && leagueCount > 0 && (
        <div className="pt-4 border-t">
          <Link
            to={`/group/${groupId}/admin#leagues`}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            View all {leagueCount} league{leagueCount !== 1 ? 's' : ''} in admin
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  )
}
